import { useFormikContext } from 'formik';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { FormLabel } from 'src/components/FormLabel';
import { Grid } from 'src/components/Grid';
import { DATES, DAY_NUMBERS } from 'src/constants';
import { FormSelect } from 'src/shared/Forms/Fields/FormSelect';
import { FormTextField } from 'src/shared/Forms/Fields/FormTextField';

import { FormValues } from './ManageSchedule';
import { PlanDays } from './PlanDays';

const gridSize = { md: 6, xs: 12 };

const gridStyle = { display: 'flex', flexDirection: 'column', gap: 1 };

type RecurringOptionsProps = {
  readOnlyDays?: boolean;
};

export const RecurringOptions = ({
  readOnlyDays = false,
}: RecurringOptionsProps) => {
  const { t } = useTranslation('translation', {
    keyPrefix: 'schedules.recurringOptions',
  });

  const { values, setFieldValue } = useFormikContext<FormValues>();
  const recurringTypes = useMemo(() => {
    if (values.recurringCount > 1) {
      return [
        { id: 'day', name: t('days') },
        { id: 'week', name: t('weeks') },
        { id: 'month', name: t('months') },
        { id: 'month_on_day', name: t('monthsOnDays') },
        { id: 'month_on_date', name: t('monthsOnDates') },
      ];
    }

    return [
      { id: 'day', name: t('day') },
      { id: 'week', name: t('week') },
      { id: 'month', name: t('month') },
      { id: 'month_on_day', name: t('monthOnDays') },
      { id: 'month_on_date', name: t('monthOnDates') },
    ];
  }, [values.recurringCount, t]);

  return (
    <Grid container spacing={2} size={{ md: 12, xs: 12 }}>
      <Grid size={gridSize} sx={gridStyle}>
        <FormLabel>{t('repeatsEvery')}</FormLabel>
        <Grid container spacing={2}>
          <Grid size={gridSize}>
            <FormTextField
              name="recurringCount"
              type="number"
              InputProps={{ inputProps: { min: 1 } }}
            />
          </Grid>
          <Grid size={gridSize}>
            <FormSelect
              name="recurringType"
              options={recurringTypes.map((type) => ({
                value: type.id,
                label: type.name,
              }))}
              size="medium"
              onChange={(e) => {
                const value = e.target.value as string;
                setFieldValue(
                  'recurringDayNumbers',
                  ['month_on_day', 'month_on_date'].includes(value) ? ['1'] : []
                );

                setFieldValue(
                  'days',
                  values.days?.filter((day) => day !== 'sunday') || []
                );

                setFieldValue('recurringType', value);
              }}
            />
          </Grid>
        </Grid>
      </Grid>

      {values.recurringType === 'week' && (
        <Grid size={gridSize} sx={gridStyle}>
          <FormLabel>{t('daysOfWeek')}</FormLabel>
          <PlanDays days={values.days} readOnly={readOnlyDays} />
        </Grid>
      )}

      {values.recurringType === 'month_on_date' && (
        <Grid size={gridSize} sx={gridStyle}>
          <FormLabel>{t('dates')}</FormLabel>
          <FormSelect
            multiple
            options={DATES.map((date) => ({
              value: date.id,
              label: date.name,
            }))}
            name="recurringDayNumbers"
            size="medium"
            MenuProps={{
              sx: {
                '& .MuiList-root': {
                  maxHeight: '300px',
                },
              },
            }}
          />
        </Grid>
      )}

      {values.recurringType === 'month_on_day' && (
        <Grid size={gridSize}>
          <Grid container spacing={2}>
            <Grid size={gridSize} sx={gridStyle}>
              <FormLabel>{t('dates')}</FormLabel>
              <FormSelect
                multiple
                options={DAY_NUMBERS.map((day) => ({
                  value: day.id,
                  label: day.name,
                }))}
                name="recurringDayNumbers"
                size="medium"
              />
            </Grid>
            <Grid size={gridSize} sx={gridStyle}>
              <FormLabel>{t('daysOfWeek')}</FormLabel>
              <PlanDays days={values.days} readOnly={readOnlyDays} />
            </Grid>
          </Grid>
        </Grid>
      )}
    </Grid>
  );
};
